import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Chip } from '../../components/ui/Chip';
import { EmptyState } from '../../components/ui/EmptyState';
import { TableRowSkeleton } from '../../components/ui/Skeleton';
import { useToast } from '../../components/ui/Toast';
import {
  useAudiences,
  useAudienceDraftPreview,
  useAvailableLists,
  useCreateAudience,
  useUpdateAudience,
  type Audience,
  type AudiencePreview,
  type AudienceRule,
  type RuleGroup,
} from '../../api/audiences.api';

/* ── Screen 05 — Audience and list management (wired) ── */

interface RuleField {
  value: string;
  label: string;
  /** Hint used by the rule UI to pick the right operator default. */
  kind?: 'string' | 'number' | 'array' | 'list_membership' | 'placement';
}

// CandidateCache columns the backend audience evaluator filters against.
// Keep these field names in sync with prisma/schema.prisma — the evaluator
// reads row[field] directly (with attributes JSON as a fallback).
const CANDIDATE_RULE_FIELDS: RuleField[] = [
  { value: 'first_name',          label: 'First name' },
  { value: 'last_name',           label: 'Last name' },
  { value: 'title',               label: 'Position title' },
  { value: 'company',             label: 'Company' },
  { value: 'location',            label: 'Location' },
  { value: 'phone',               label: 'Phone' },
  { value: 'email',               label: 'Email' },
  { value: 'stage',               label: 'Stage' },
  { value: 'tags',                label: 'Tags', kind: 'array' },
  { value: 'availability_status', label: 'Availability' },
  { value: 'skills',              label: 'Skills', kind: 'array' },
  { value: 'notice_period',       label: 'Notice period' },
  { value: 'salary_min',          label: 'Salary expectation (min)', kind: 'number' },
  { value: 'salary_max',          label: 'Salary expectation (max)', kind: 'number' },
  { value: '_list_ids',                       label: 'On list', kind: 'list_membership' },
  // Placement filters. _has_placement is the yes/no toggle; the array
  // fields use the `contains` op against any placement on the candidate.
  // _latest_placement_start_date works with `within_days` to grab recent
  // placements specifically.
  { value: '_has_placement',                  label: 'Has placement', kind: 'placement' },
  { value: '_placement_employment_types',     label: 'Placement: employment type', kind: 'array' },
  { value: '_placement_job_titles',           label: 'Placement: job title', kind: 'array' },
  { value: '_placement_employer_companies',   label: 'Placement: client / employer', kind: 'array' },
  { value: '_placement_statuses',             label: 'Placement: status', kind: 'array' },
  { value: '_latest_placement_start_date',    label: 'Latest placement: start date' },
];

// Contact (CRM) fields. Subset of candidate fields — no skills / availability /
// salary / placements / stage. The backend audiences.service.ts evaluates rules
// against the matching cache table by audience.type, so these names must align
// with ContactCache columns in prisma/schema.prisma.
const CONTACT_RULE_FIELDS: RuleField[] = [
  { value: 'first_name',   label: 'First name' },
  { value: 'last_name',    label: 'Last name' },
  { value: 'title',        label: 'Title' },
  { value: 'company',      label: 'Company' },
  { value: 'location',     label: 'Location' },
  { value: 'phone',        label: 'Phone' },
  { value: 'email',        label: 'Email' },
  { value: 'tags',         label: 'Tags', kind: 'array' },
  { value: 'contact_type', label: 'Contact type' },
  { value: '_list_ids',    label: 'On list', kind: 'list_membership' },
];

// Marketing audiences target the org's MarketingContact table (Section 10.4).
// Field set is deliberately smaller than candidates/contacts — marketing
// contacts are an opt-in list, not a CRM snapshot, so cohort filters tend to
// be email/name/title-shaped only.
const MARKETING_RULE_FIELDS: RuleField[] = [
  { value: 'first_name',     label: 'First name' },
  { value: 'last_name',      label: 'Last name' },
  { value: 'title',          label: 'Title' },
  { value: 'email',          label: 'Email' },
  { value: 'opt_in_method',  label: 'Opt-in method' },
];

function fieldsForAudienceType(audienceType: string | null | undefined): RuleField[] {
  if (audienceType === 'contacts') return CONTACT_RULE_FIELDS;
  if (audienceType === 'marketing') return MARKETING_RULE_FIELDS;
  return CANDIDATE_RULE_FIELDS;
}

/**
 * Default operator for a newly-selected field. Numeric fields default to
 * `gte` (most common comparison); arrays / list membership default to
 * `contains`; placement is a true/false equality check. Everything else
 * keeps the current value.
 */
function defaultOpForKind(kind: RuleField['kind']): AudienceRule['op'] {
  switch (kind) {
    case 'number': return 'gte';
    case 'array':
    case 'list_membership': return 'contains';
    case 'placement': return 'equals';
    default: return 'contains';
  }
}

function defaultValueForKind(kind: RuleField['kind']): AudienceRule['value'] {
  switch (kind) {
    case 'placement': return 'true';
    case 'number': return '';
    default: return '';
  }
}

const RULE_OPS: { value: AudienceRule['op']; label: string }[] = [
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'doesn’t contain' },
  { value: 'equals', label: 'is' },
  { value: 'not_equals', label: 'is not' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'in', label: 'is one of (csv)' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'within_days', label: 'within last N days' },
  { value: 'exists', label: 'is set' },
  { value: 'missing', label: 'is missing' },
];

const DEFAULT_GROUP: RuleGroup = { combinator: 'AND', include: [], exclude: [] };

function normaliseGroup(raw: unknown): RuleGroup {
  if (Array.isArray(raw)) return { combinator: 'AND', include: raw as AudienceRule[], exclude: [] };
  if (raw && typeof raw === 'object') {
    const g = raw as Partial<RuleGroup>;
    return {
      combinator: g.combinator ?? 'AND',
      include: Array.isArray(g.include) ? g.include : [],
      exclude: Array.isArray(g.exclude) ? g.exclude : [],
    };
  }
  return DEFAULT_GROUP;
}

export function AudiencesPage() {
  const { data, isLoading } = useAudiences();
  const audiences = data?.data ?? [];

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<'candidates' | 'contacts' | 'marketing'>('candidates');
  // Dynamic = membership re-evaluated each time the audience is read (live cohort).
  // Static = members snapshotted at launch time (frozen list). Default to dynamic
  // so newly synced JT data flows through without needing a manual refresh.
  const [newIsDynamic, setNewIsDynamic] = useState(true);

  const createAudience = useCreateAudience();
  const { addToast } = useToast();

  useEffect(() => {
    if (!selectedId && audiences.length > 0) setSelectedId(audiences[0].id);
  }, [audiences, selectedId]);

  const selected = audiences.find((a) => a.id === selectedId) ?? null;

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const created = await createAudience.mutateAsync({
        name: newName.trim(),
        type: newType,
        rules: DEFAULT_GROUP,
        is_dynamic: newIsDynamic,
      });
      addToast({ variant: 'success', title: 'Audience created' });
      setSelectedId(created.id);
      setShowCreate(false);
      setNewName('');
    } catch (err) {
      // Surface the actual server message so the user can act on it. Falls
      // back to the network-layer message, then a generic line.
      const e = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
      const detail = e.response?.data?.message
        ?? e.response?.data?.error
        ?? e.message
        ?? 'Unknown error';
      // eslint-disable-next-line no-console
      console.error('audience create failed', err);
      addToast({ variant: 'danger', title: 'Could not create audience', description: detail });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <Breadcrumb />
      <HeadingRow onNew={() => setShowCreate((s) => !s)} />

      {showCreate && (
        <NewAudienceInline
          name={newName}
          onName={setNewName}
          type={newType}
          onType={setNewType}
          isDynamic={newIsDynamic}
          onIsDynamic={setNewIsDynamic}
          onCancel={() => {
            setShowCreate(false);
            setNewName('');
          }}
          onCreate={handleCreate}
          isPending={createAudience.isPending}
        />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 320px) minmax(0, 1fr)', gap: 'var(--space-6)', alignItems: 'flex-start' }}>
        <AudienceListPanel
          audiences={audiences}
          selectedId={selectedId}
          onSelect={setSelectedId}
          isLoading={isLoading}
        />
        {selected ? (
          <AudienceDetailPanel audience={selected} />
        ) : (
          <div
            style={{
              backgroundColor: 'var(--card-bg)',
              border: '1px solid var(--card-border)',
              borderRadius: 'var(--card-radius)',
              boxShadow: 'var(--card-shadow)',
              padding: 'var(--space-6)',
            }}
          >
            {audiences.length === 0 && !isLoading ? (
              <EmptyState
                icon={<IconUsers size={20} />}
                title="No audiences yet"
                description="Create an audience and define rules that match candidates or contacts in your JT data."
                action={<PrimaryActionButton onClick={() => setShowCreate(true)}>Create audience</PrimaryActionButton>}
              />
            ) : (
              <p style={{ fontFamily: 'var(--font-sans)', color: 'var(--color-text-muted)' }}>Select an audience to view its rules and members.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── List panel ── */

function AudienceListPanel({
  audiences,
  selectedId,
  onSelect,
  isLoading,
}: {
  audiences: Audience[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  isLoading: boolean;
}) {
  return (
    <section
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        overflow: 'hidden',
      }}
    >
      <header
        style={{
          padding: 'var(--space-4) var(--space-5)',
          borderBottom: '1px solid var(--color-border-default)',
        }}
      >
        <h2 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-brand)' }}>Audiences</h2>
      </header>
      {isLoading ? (
        <div style={{ padding: 'var(--space-4)' }}>
          <TableRowSkeleton rows={4} />
        </div>
      ) : audiences.length === 0 ? (
        <p style={{ padding: 'var(--space-4)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>No audiences yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {audiences.map((a, i) => {
            const isSelected = a.id === selectedId;
            return (
              <li
                key={a.id}
                style={{
                  borderTop: i === 0 ? 'none' : '1px solid var(--color-border-default)',
                  backgroundColor: isSelected ? 'var(--color-blue-50)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'background-color var(--transition-base)',
                }}
                onClick={() => onSelect(a.id)}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--table-row-hover-bg)';
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div style={{ padding: 'var(--space-3) var(--space-4)', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: isSelected ? 'var(--color-text-brand)' : 'var(--color-text-primary)' }}>
                    {a.name}
                  </span>
                  <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                    {a.member_count.toLocaleString()} members · {a.is_dynamic ? 'Dynamic' : 'Static'} · {a.type}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/* ── Detail panel ── */

function AudienceDetailPanel({ audience }: { audience: Audience }) {
  const initialGroup = normaliseGroup(audience.rules);
  const [group, setGroup] = useState<RuleGroup>(initialGroup);
  const [dirty, setDirty] = useState(false);
  // Preview is held locally and driven by the "Review" button (or Enter in a
  // rule value). It evaluates the *draft* rules against JT data WITHOUT
  // persisting them — saving the audience is a separate, explicit action.
  const [preview, setPreview] = useState<AudiencePreview | undefined>(undefined);
  // Whether the shown preview reflects the current draft. Editing a rule
  // invalidates it until the user reviews again.
  const [previewStale, setPreviewStale] = useState(false);

  const update = useUpdateAudience(audience.id);
  const draftPreview = useAudienceDraftPreview();
  const { addToast } = useToast();

  const runReview = async (g: RuleGroup) => {
    try {
      const result = await draftPreview.mutateAsync({ type: audience.type, rules: g });
      setPreview(result);
      setPreviewStale(false);
    } catch {
      addToast({ variant: 'danger', title: 'Could not preview audience' });
    }
  };

  // Reset state + show the saved audience's current matches when switching
  // audience. The initial preview reflects what's persisted; subsequent edits
  // mark it stale until the user clicks Review.
  useEffect(() => {
    const g = normaliseGroup(audience.rules);
    setGroup(g);
    setDirty(false);
    setPreviewStale(false);
    runReview(g);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience.id]);

  // Any rule edit marks the local state dirty (Save becomes active) and the
  // shown preview stale (Review re-runs it).
  const markEdited = () => {
    setDirty(true);
    setPreviewStale(true);
  };

  const setIncludeRule = (i: number, patch: Partial<AudienceRule>) => {
    setGroup((g) => ({ ...g, include: g.include.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
    markEdited();
  };
  const setExcludeRule = (i: number, patch: Partial<AudienceRule>) => {
    setGroup((g) => ({ ...g, exclude: g.exclude.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
    markEdited();
  };
  const addInclude = () => {
    setGroup((g) => ({ ...g, include: [...g.include, { field: 'title', op: 'contains', value: '' }] }));
    markEdited();
  };
  const addExclude = () => {
    setGroup((g) => ({ ...g, exclude: [...g.exclude, { field: 'email', op: 'contains', value: '' }] }));
    markEdited();
  };
  const removeInclude = (i: number) => {
    setGroup((g) => ({ ...g, include: g.include.filter((_, j) => j !== i) }));
    markEdited();
  };
  const removeExclude = (i: number) => {
    setGroup((g) => ({ ...g, exclude: g.exclude.filter((_, j) => j !== i) }));
    markEdited();
  };

  const save = async () => {
    try {
      await update.mutateAsync({ rules: group });
      addToast({ variant: 'success', title: 'Audience saved', description: `${preview?.total ?? audience.member_count} members matched.` });
      setDirty(false);
    } catch {
      addToast({ variant: 'danger', title: 'Could not save rules' });
    }
  };

  return (
    <section
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        padding: 'var(--space-6)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-5)',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)' }}>
            {audience.name}
          </h2>
          <p style={{ marginTop: 'var(--space-1)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <Chip variant={audience.is_dynamic ? 'live' : 'idle'}>{audience.is_dynamic ? 'Dynamic' : 'Static'}</Chip>
            Source: {audience.type}
            {' · '}
            {audience.member_count.toLocaleString()} members
            {audience.last_synced_at && (
              <>
                {' · '}
                Synced {new Date(audience.last_synced_at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}
              </>
            )}
          </p>
          <label style={{ marginTop: 'var(--space-2)', display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            <span>Membership mode:</span>
            <select
              value={audience.is_dynamic ? 'dynamic' : 'static'}
              onChange={async (e) => {
                const nextDynamic = e.target.value === 'dynamic';
                if (nextDynamic === audience.is_dynamic) return;
                try {
                  await update.mutateAsync({ is_dynamic: nextDynamic });
                  addToast({
                    variant: 'success',
                    title: nextDynamic ? 'Switched to dynamic' : 'Switched to static',
                    description: nextDynamic
                      ? 'Members will be re-evaluated each run.'
                      : 'Members are now snapshotted at launch.',
                  });
                } catch (err) {
                  addToast({ variant: 'danger', title: 'Could not change membership mode', description: (err as Error).message });
                }
              }}
              style={ruleSelectStyle()}
            >
              <option value="dynamic">Dynamic</option>
              <option value="static">Static</option>
            </select>
          </label>
        </div>
        <PrimaryActionButton onClick={save} disabled={!dirty || update.isPending}>
          {update.isPending ? 'Saving…' : 'Save rules'}
        </PrimaryActionButton>
      </header>

      <RuleSection
        title={`Include ${audience.type === 'contacts' ? 'contacts' : 'candidates'} that match`}
        subtitle={group.combinator === 'AND' ? 'All rules must match (AND)' : 'Any rule may match (OR)'}
        rules={group.include}
        fields={fieldsForAudienceType(audience.type)}
        entityType={audience.type === 'contacts' ? 'contact' : 'candidate'}
        onChange={setIncludeRule}
        onAdd={addInclude}
        onRemove={removeInclude}
        onReview={() => runReview(group)}
        combinator={group.combinator}
        onCombinator={(c) => {
          setGroup((g) => ({ ...g, combinator: c }));
          markEdited();
        }}
      />

      <RuleSection
        title={`Exclude ${audience.type === 'contacts' ? 'contacts' : 'candidates'} that match`}
        subtitle="Any matching rule will exclude (OR)"
        rules={group.exclude}
        fields={fieldsForAudienceType(audience.type)}
        entityType={audience.type === 'contacts' ? 'contact' : 'candidate'}
        onChange={setExcludeRule}
        onAdd={addExclude}
        onRemove={removeExclude}
        onReview={() => runReview(group)}
      />

      <PreviewSection
        preview={preview}
        loading={draftPreview.isPending}
        savedCount={audience.member_count}
        dirty={dirty}
        stale={previewStale}
        // "Review" evaluates the current draft rules against JT data WITHOUT
        // saving — building and refining the cohort is fully decoupled from
        // committing it. Saving is the separate "Save rules" action above.
        onReview={() => runReview(group)}
      />
    </section>
  );
}

/* ── Rule section ── */

function RuleSection({
  title,
  subtitle,
  rules,
  fields,
  entityType,
  onChange,
  onAdd,
  onRemove,
  onReview,
  combinator,
  onCombinator,
}: {
  title: string;
  subtitle: string;
  rules: AudienceRule[];
  fields: RuleField[];
  /** 'candidate' | 'contact' — used by the list-membership picker to fetch
   *  the right list set. Derived from audience.type. */
  entityType: 'candidate' | 'contact';
  onChange: (i: number, patch: Partial<AudienceRule>) => void;
  onAdd: () => void;
  onRemove: (i: number) => void;
  /** Pressing Enter inside a rule value triggers a (non-saving) review. */
  onReview: () => void;
  combinator?: 'AND' | 'OR';
  onCombinator?: (c: 'AND' | 'OR') => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div>
          <h3 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>
            {title}
          </h3>
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{subtitle}</p>
        </div>
        {combinator && onCombinator && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Match:</span>
            <select
              value={combinator}
              onChange={(e) => onCombinator(e.target.value as 'AND' | 'OR')}
              aria-label="Combinator"
              style={smallSelectStyle()}
            >
              <option value="AND">AND</option>
              <option value="OR">OR</option>
            </select>
          </div>
        )}
      </div>

      {rules.length === 0 && (
        <p style={{ padding: 'var(--space-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-surface-muted)', borderRadius: 'var(--radius-input)', border: '1px dashed var(--color-border-strong)' }}>
          No rules yet: every {''}{rules.length === 0 ? 'matching record' : 'rule'} passes.
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {rules.map((rule, i) => {
          const fieldDef = fields.find((f) => f.value === rule.field) ?? fields[0];
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-input)',
                border: '1px solid var(--color-border-default)',
                backgroundColor: 'var(--color-surface-white)',
              }}
            >
              <select
                value={rule.field}
                onChange={(e) => {
                  const nextField = e.target.value;
                  const nextDef = fields.find((f) => f.value === nextField);
                  // Reset op + value when switching to a different kind, so a
                  // "title contains React" rule doesn't end up as
                  // "_has_placement contains React" after a field swap.
                  const fieldKindChanged = nextDef?.kind !== fieldDef?.kind;
                  onChange(i, fieldKindChanged
                    ? { field: nextField, op: defaultOpForKind(nextDef?.kind), value: defaultValueForKind(nextDef?.kind) }
                    : { field: nextField }
                  );
                }}
                aria-label="Field"
                style={ruleSelectStyle()}
              >
                {fields.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>

              {/* Operator picker is suppressed for placement (always equals)
                  and list_membership (always contains) — those fields have
                  exactly one sensible op. */}
              {fieldDef?.kind !== 'placement' && fieldDef?.kind !== 'list_membership' && (
                <select value={rule.op} onChange={(e) => onChange(i, { op: e.target.value as AudienceRule['op'] })} aria-label="Operator" style={ruleSelectStyle()}>
                  {RULE_OPS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              )}

              <RuleValueInput
                rule={rule}
                fieldDef={fieldDef}
                entityType={entityType}
                onChange={(patch) => onChange(i, patch)}
                onReview={onReview}
              />

              <button
                type="button"
                onClick={() => onRemove(i)}
                aria-label="Remove rule"
                style={{
                  width: 'var(--btn-height-sm)',
                  height: 'var(--btn-height-sm)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 'var(--btn-radius)',
                  backgroundColor: 'transparent',
                  color: 'var(--color-text-muted)',
                  border: '1px solid var(--color-border-default)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-sans)',
                }}
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>

      <SecondaryActionButton onClick={onAdd}>+ Add rule</SecondaryActionButton>
    </div>
  );
}

/* ── Preview ── */

function PreviewSection({
  preview,
  loading,
  savedCount,
  dirty,
  stale,
  onReview,
}: {
  preview: { members: { id: string; name: string; email: string; title?: string; company?: string }[]; total: number; sampled: boolean } | undefined;
  loading: boolean;
  savedCount: number;
  dirty: boolean;
  /** True when the rules have changed since the last Review — the shown
   *  counts no longer reflect the current draft. */
  stale: boolean;
  onReview: () => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <h3 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', margin: 0 }}>
            Preview
          </h3>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            {stale
              ? 'Rules changed: click Review to refresh the match count. Nothing is saved until you click “Save rules”.'
              : 'Showing matches for the rules below. Refine freely: reviewing never saves the audience.'}
          </span>
        </div>
        <SecondaryActionButton onClick={onReview} disabled={loading}>
          {loading ? 'Reviewing…' : stale ? 'Review' : 'Review again'}
        </SecondaryActionButton>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <CountStat label="Saved members" value={savedCount} />
        <CountStat label={dirty ? 'Draft match' : 'Live match'} value={preview?.total ?? 0} highlight />
      </div>

      {loading ? (
        <TableRowSkeleton rows={3} />
      ) : !preview || preview.members.length === 0 ? (
        <p style={{ padding: 'var(--space-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-surface-muted)', borderRadius: 'var(--radius-input)', border: '1px dashed var(--color-border-strong)' }}>
          No matches with the current rules. Try loosening filters or run JT sync to populate the cache.
        </p>
      ) : (
        <div style={{ border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-input)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)' }}>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Title</Th>
                <Th>Company</Th>
                <Th>Email</Th>
              </tr>
            </thead>
            <tbody>
              {preview.members.map((m, i) => (
                <tr key={m.id} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--color-border-default)' }}>
                  <Td>
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>{m.name}</span>
                  </Td>
                  <Td>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{m.title ?? '-'}</span>
                  </Td>
                  <Td>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{m.company ?? '-'}</span>
                  </Td>
                  <Td>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{m.email}</span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {preview?.sampled && (
        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          Showing first 25 matches of {preview.total.toLocaleString()}.
        </p>
      )}
    </div>
  );
}

function CountStat({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div
      style={{
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-input)',
        backgroundColor: highlight ? 'var(--color-blue-50)' : 'var(--color-surface-muted)',
        border: `1px solid ${highlight ? 'var(--color-blue-100)' : 'var(--color-border-default)'}`,
        minWidth: '140px',
      }}
    >
      <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>
        {label}
      </p>
      <p style={{ marginTop: '2px', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', color: highlight ? 'var(--color-brand-blue)' : 'var(--color-text-primary)' }}>
        {value.toLocaleString()}
      </p>
    </div>
  );
}

/* ── Header ── */

function Breadcrumb() {
  return (
    <nav aria-label="Breadcrumb">
      <ol style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', listStyle: 'none', margin: 0, padding: 0 }}>
        <li>
          <Link to="/dashboard" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)', textDecoration: 'none' }}>
            Engage
          </Link>
        </li>
        <li aria-hidden="true" style={{ color: 'var(--color-text-subtle)' }}>
          <IconChevronRight size={12} />
        </li>
        <li aria-current="page" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>
          Audiences
        </li>
      </ol>
    </nav>
  );
}

function HeadingRow({ onNew }: { onNew: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
      <div>
        <h1
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-4xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-text-brand)',
            letterSpacing: 'var(--tracking-tight)',
            lineHeight: 'var(--leading-tight)',
          }}
        >
          Audiences
        </h1>
        <p style={{ marginTop: 'var(--space-1)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', color: 'var(--color-text-muted)' }}>
          Reusable lists driven by rules over your synced JT data.
        </p>
      </div>
      <PrimaryActionButton onClick={onNew}>
        <IconPlus size={14} />
        <span style={{ marginLeft: 'var(--space-1)' }}>New audience</span>
      </PrimaryActionButton>
    </div>
  );
}

function NewAudienceInline({
  name,
  onName,
  type,
  onType,
  isDynamic,
  onIsDynamic,
  onCancel,
  onCreate,
  isPending,
}: {
  name: string;
  onName: (v: string) => void;
  type: 'candidates' | 'contacts' | 'marketing';
  onType: (v: 'candidates' | 'contacts' | 'marketing') => void;
  isDynamic: boolean;
  onIsDynamic: (v: boolean) => void;
  onCancel: () => void;
  onCreate: () => void;
  isPending: boolean;
}) {
  return (
    <div
      style={{
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-input)',
        backgroundColor: 'var(--color-blue-50)',
        border: '1px solid var(--color-blue-100)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
      }}
    >
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Audience name (e.g. Backend Sydney)"
          value={name}
          onChange={(e) => onName(e.target.value)}
          style={{ flex: 1, minWidth: '200px', ...ruleInputStyle() }}
        />
        <select value={type} onChange={(e) => onType(e.target.value as typeof type)} aria-label="Source" style={ruleSelectStyle()}>
          <option value="candidates">Candidates</option>
          <option value="contacts">Contacts</option>
          <option value="marketing">Marketing contacts (newsletter)</option>
          {/* Applicants source type deferred to post-MVP pending JT backend work. */}
        </select>
        <select
          value={isDynamic ? 'dynamic' : 'static'}
          onChange={(e) => onIsDynamic(e.target.value === 'dynamic')}
          aria-label="Membership mode"
          style={ruleSelectStyle()}
        >
          <option value="dynamic">Dynamic (re-evaluate each run)</option>
          <option value="static">Static (snapshot at launch)</option>
        </select>
        <PrimaryActionButton onClick={onCreate} disabled={!name.trim() || isPending}>
          {isPending ? 'Creating…' : 'Create'}
        </PrimaryActionButton>
        <SecondaryActionButton onClick={onCancel}>Cancel</SecondaryActionButton>
      </div>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
        {isDynamic
          ? 'Dynamic: anyone matching the rules at send time is enrolled. New JT records flow in automatically.'
          : 'Static: members are captured once at campaign launch and the list never changes.'}
      </span>
    </div>
  );
}

/* ── Building blocks ── */

function Th({ children }: { children: ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        height: 'var(--table-header-height)',
        padding: '0 var(--table-cell-padding-x)',
        backgroundColor: 'var(--table-header-bg)',
        color: 'var(--table-header-text)',
        fontSize: 'var(--table-header-size)',
        fontWeight: 'var(--table-header-weight)',
        letterSpacing: 'var(--table-header-tracking)',
        textTransform: 'uppercase',
        borderBottom: '1px solid var(--color-border-default)',
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: ReactNode }) {
  return (
    <td style={{ padding: 'var(--table-cell-padding-y) var(--table-cell-padding-x)', verticalAlign: 'middle', height: 'var(--table-row-height)' }}>
      {children}
    </td>
  );
}

function PrimaryActionButton({ children, onClick, disabled }: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 'var(--btn-height)',
        padding: '0 var(--btn-padding-x)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-primary)',
        color: 'var(--color-action-primary-text)',
        border: 'none',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--btn-font-size)',
        fontWeight: 'var(--btn-font-weight)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function SecondaryActionButton({ children, onClick, disabled }: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 'var(--btn-height-sm)',
        padding: '0 var(--btn-padding-x-sm)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-secondary)',
        color: 'var(--color-action-secondary-text)',
        border: '1px solid var(--color-action-secondary-border)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

/**
 * Smart value input for a rule row. Switches widget based on the field's
 * declared `kind`:
 *   - list_membership: dropdown of synced JT lists (filtered by entityType)
 *   - placement:       true/false toggle for "has any placement"
 *   - number:          number input
 *   - exists/missing:  no input
 *   - default:         free-text input
 */
function RuleValueInput({
  rule,
  fieldDef,
  entityType,
  onChange,
  onReview,
}: {
  rule: AudienceRule;
  fieldDef: RuleField;
  entityType: 'candidate' | 'contact';
  onChange: (patch: Partial<AudienceRule>) => void;
  /** Pressing Enter in a free-text/number value reviews the draft. */
  onReview: () => void;
}) {
  const reviewOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onReview();
    }
  };
  // Always-on hook — React Query gates by enabled internally. Cached at
  // staleTime so the same dropdown appearing in multiple rules doesn't
  // refetch.
  const { data: lists } = useAvailableLists(fieldDef.kind === 'list_membership' ? entityType : undefined);

  if (rule.op === 'exists' || rule.op === 'missing') return null;

  if (fieldDef.kind === 'list_membership') {
    return (
      <select
        value={typeof rule.value === 'string' ? rule.value : ''}
        onChange={(e) => onChange({ value: e.target.value })}
        aria-label="List"
        style={{ ...ruleSelectStyle(), flex: 1 }}
      >
        <option value="">Select a list…</option>
        {(lists ?? []).map((l) => (
          <option key={l.id} value={l.id}>{l.name}</option>
        ))}
      </select>
    );
  }

  if (fieldDef.kind === 'placement') {
    return (
      <select
        value={String(rule.value ?? 'true')}
        onChange={(e) => onChange({ value: e.target.value })}
        aria-label="Has placement"
        style={{ ...ruleSelectStyle(), flex: 1 }}
      >
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }

  if (fieldDef.kind === 'number') {
    return (
      <input
        type="number"
        placeholder="Value"
        value={typeof rule.value === 'string' || typeof rule.value === 'number' ? String(rule.value) : ''}
        onChange={(e) => onChange({ value: e.target.value })}
        onKeyDown={reviewOnEnter}
        style={{ ...ruleInputStyle(), flex: 1 }}
      />
    );
  }

  return (
    <input
      type="text"
      placeholder="Value: press Enter to review"
      value={typeof rule.value === 'string' || typeof rule.value === 'number' ? String(rule.value) : ''}
      onChange={(e) => onChange({ value: e.target.value })}
      onKeyDown={reviewOnEnter}
      style={{ ...ruleInputStyle(), flex: 1 }}
    />
  );
}

function ruleSelectStyle(): React.CSSProperties {
  return {
    height: 'var(--btn-height-sm)',
    padding: '0 var(--space-3)',
    border: '1px solid var(--input-border)',
    borderRadius: 'var(--input-radius)',
    backgroundColor: 'var(--input-bg)',
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
  };
}

function smallSelectStyle(): React.CSSProperties {
  return {
    height: 'var(--btn-height-sm)',
    padding: '0 var(--space-2)',
    border: '1px solid var(--input-border)',
    borderRadius: 'var(--input-radius)',
    backgroundColor: 'var(--input-bg)',
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--text-xs)',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
  };
}

function ruleInputStyle(): React.CSSProperties {
  return {
    height: 'var(--btn-height-sm)',
    padding: '0 var(--space-3)',
    border: '1px solid var(--input-border)',
    borderRadius: 'var(--input-radius)',
    backgroundColor: 'var(--input-bg)',
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-primary)',
    outline: 'none',
  };
}

function IconChevronRight({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>; }
function IconPlus({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>; }
function IconUsers({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>; }
